import { ORDER_DETAILS_PAGE_SCAN_SUBMIT_INTENTS } from '../constants/orderDetailsPageScanSubmitIntents.const';
import { SUBMIT_ORDER_DETAILS_PAGE_ACTION_NAME } from '../constants/scanActionNames.const';
import { SCAN_SUBMIT_TARGET_REGISTRY } from '../constants/scanSubmitTargetRegistry.const';
import {
  type CreateSubmitOrderDetailsPageScanActionOptions,
  type OrderDetailsPageScanSubmitIntent,
} from '../types/orderDetailsPageScanSubmit.type';
import { type ScanAction, type ScanActionResult } from '../types/scanAction.type';
import { type ScanBarcodeContent } from '../types/scanContext.type';
import { type ScanEvent } from '../types/scanEvent.type';
import { type ScanSubmitTarget } from '../types/scanSubmitTarget.type';
import { matchesScannedOrderShipment } from '../utils/matchesScannedOrderShipment.util';
import { resolveOrderDetailsPageRoute } from '../utils/resolveOrderDetailsPageRoute.util';

const findTarget = (
  targets: readonly ScanSubmitTarget[],
  intent: OrderDetailsPageScanSubmitIntent,
  barcodeContent: ScanBarcodeContent,
): ScanSubmitTarget | undefined =>
  targets.find(
    (target) => target.intent === intent && matchesScannedOrderShipment(target, barcodeContent),
  );

export const createSubmitOrderDetailsPageScanAction = ({
  registry = SCAN_SUBMIT_TARGET_REGISTRY,
}: CreateSubmitOrderDetailsPageScanActionOptions = {}): ScanAction => ({
  execute: (scanEvent: ScanEvent): ScanActionResult => {
    const barcodeContent = scanEvent.scan_context.barcode_content;

    if (!barcodeContent) return { success: true };

    const targets = registry.snapshot();
    // Checkout keeps both intents registered, so buying wins over reopening.
    const target =
      findTarget(
        targets,
        ORDER_DETAILS_PAGE_SCAN_SUBMIT_INTENTS.COMPLETE_CHECKOUT,
        barcodeContent,
      ) ??
      findTarget(targets, ORDER_DETAILS_PAGE_SCAN_SUBMIT_INTENTS.START_CHECKOUT, barcodeContent);

    if (!target) return { success: true };

    if (target.isSubmitting) return { success: true };

    if (!target.canSubmit) {
      if (target.blocker) target.onBlocked(target.blocker);

      return { success: true };
    }

    target.submit();

    return { success: true };
  },

  matches: (scanEvent: ScanEvent): boolean => {
    if (!scanEvent.scan_context.barcode_content) return false;

    const route = resolveOrderDetailsPageRoute(scanEvent.scan_context.route.raw_path);

    if (!route) return false;

    return registry
      .snapshot()
      .some(
        (target) =>
          target.orderObjectId === route.orderObjectId &&
          target.batchEntryId === route.batchEntryId,
      );
  },

  name: SUBMIT_ORDER_DETAILS_PAGE_ACTION_NAME,
});
